import { useTheme, Box, InputBase } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { SearchRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

const DBSearch = ({
  param,
  onSearch,
  noInitialLoad,
  sort = null,
  children,
  limit = 25,
  sx,
  value,
  hideSearchBar,
  filter,
  className,
  onSearchChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const scrollContainerRef = useRef(null);

  const cookies = new Cookies();
  const prefix = cookies.get('searchPrefix');

  const [search, setSearch] = useState(value || '');
  const [searchResult, setSearchResult] = useState(null);
  const [hits, setHits] = useState([]);

  // Tracks the query of the most recently issued request so we can drop
  // stale responses (e.g. auto-loaded next page of a query the user has
  // since typed past).
  const latestQueryRef = useRef(value || '');

  useEffect(() => {
    if (!noInitialLoad) handleSearch({ target: { value: value || '' } });
  }, [config, value]);

  function handleSearch(event, newPage, keepHits = false) {
    if (!config) return;

    const query = event ? event.target.value : search;
    latestQueryRef.current = query;
    const searchData = {
      q: query,
      ...(sort && !query?.trim() && { sort: [`${sort}:asc`] }),
      page: newPage || 1,
      hitsPerPage: limit,
      filter,
    };
    SearchRequest({
      baseUrl: config.searchUrl,
      path: `indexes/${prefix}${param}/search`,
      method: 'post',
      data: searchData,
      onResponse: (res) => {
        // Drop stale responses (the user has moved on to a newer query).
        if (latestQueryRef.current !== query) return;
        onSearch(keepHits ? [...hits, ...res.hits] : res.hits);
        setSearchResult(res);
        setHits(keepHits ? [...hits, ...res.hits] : res.hits);
      },
    });
    if (event) {
      setSearch(event.target.value);
      if (onSearchChange) onSearchChange(event.target.value);
    }
  }

  return (
    <Box
      ref={scrollContainerRef}
      sx={sx}
      className={
        'relative flex flex-col gap-2 h-full w-full overflow-y-auto pr-2 overflow-x-hidden'
      }
    >
      {!hideSearchBar && (
        <Box
          className="flex backdrop-blur-lg py-1 sticky top-0 z-10"
          borderRadius="3px"
          backgroundColor={colors.bgInput}
        >
          <InputBase
            sx={{ ml: 2, flex: 1 }}
            placeholder={t('Search')}
            value={search}
            onChange={handleSearch}
            className="w-full"
          />
        </Box>
      )}
      {children && searchResult && (
        <InfiniteScroll
          scrollContainerRef={scrollContainerRef}
          searchResult={searchResult}
          onLoadMore={(newPage) => handleSearch(null, newPage, true)}
        >
          {children}
        </InfiniteScroll>
      )}
    </Box>
  );
};

const InfiniteScroll = ({
  children,
  scrollContainerRef,
  searchResult,
  onLoadMore,
  triggerDist = 500,
  className,
}) => {
  // Tracks the page currently being fetched. Cleared on every searchResult
  // update so a fresh search (which resets page back to 1) doesn't leave
  // pagination permanently gated.
  const inFlightPageRef = useRef(null);

  useEffect(() => {
    inFlightPageRef.current = null;
  }, [searchResult]);

  useEffect(() => {
    if (!scrollContainerRef || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;

    const checkRemaining = (el) =>
      el.scrollHeight - el.clientHeight - el.scrollTop;

    // Find the first descendant that's actually scrolling, if any.
    // Children like ExoTable own their own internal overflow:auto, so the
    // wrapper itself never scrolls and its scrollHeight matches clientHeight.
    const findActiveScroller = () => {
      const queue = [container];
      while (queue.length) {
        const el = queue.shift();
        if (el !== container && el.scrollHeight > el.clientHeight) return el;
        for (const child of el.children) queue.push(child);
      }
      return null;
    };

    const maybeLoadMore = (event) => {
      // On scroll events use the element that actually scrolled (capture
      // phase delivers events from descendants). Otherwise check whichever
      // element in the tree is the real scroller; if nothing is scrollable
      // yet, content doesn't fill the view → load more.
      let remainingScroll;
      if (event && event.target instanceof Element) {
        remainingScroll = checkRemaining(event.target);
      } else {
        const scroller = findActiveScroller();
        remainingScroll = scroller ? checkRemaining(scroller) : 0;
      }

      const totalPages = searchResult.totalPages;
      const currentPage = searchResult.page;

      if (
        triggerDist > remainingScroll &&
        currentPage < totalPages &&
        inFlightPageRef.current === null
      ) {
        inFlightPageRef.current = currentPage + 1;
        onLoadMore(currentPage + 1);
      }
    };

    // Trigger immediately in case content doesn't fill the visible area
    // yet (no scrollbar => scroll event would never fire).
    maybeLoadMore();

    // Capture phase so we catch scroll events on any descendant
    // (scroll events do not bubble).
    container.addEventListener('scroll', maybeLoadMore, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => maybeLoadMore());
      resizeObserver.observe(container);
    }

    return () => {
      container.removeEventListener('scroll', maybeLoadMore, true);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [scrollContainerRef, searchResult]);

  return <>{children}</>;
};

export default DBSearch;
